import type { StatutEcheance, StatutPaiement, StatutTache } from './domaine'

// Machine d'états : une table de transitions en données et une fonction pure
// (état, événement) -> (nouvel état, effets). Les services appliquent les effets
// dans la même transaction SQL. « En retard » n'est jamais un statut.

export type EvenementEcheance =
  | { type: 'tache_demarree' }
  | { type: 'conditions_remplies' }
  | { type: 'marquer_fait' }
  | { type: 'sans_objet' }
  | { type: 'defaire'; tachesDemarrees: boolean }
  | { type: 'tache_rouverte' }
  | { type: 'paiement_rejete' }

export type EffetEcheance =
  | 'annuler_taches_ouvertes'
  | 'annuler_paiement_ouvert'
  | 'restaurer_annules'
  | 'incrementer_cycle'
  | 'creer_tache_urgente'

type LigneTransition = {
  evenement: EvenementEcheance['type']
  depuis: readonly StatutEcheance[]
  vers: StatutEcheance | ((e: EvenementEcheance) => StatutEcheance)
  effets: readonly EffetEcheance[]
}

export const TRANSITIONS_ECHEANCE: readonly LigneTransition[] = [
  {
    evenement: 'tache_demarree',
    depuis: ['a_venir', 'en_preparation'],
    vers: 'en_preparation',
    effets: [],
  },
  {
    evenement: 'conditions_remplies',
    depuis: ['a_venir', 'en_preparation'],
    vers: 'faite',
    effets: [],
  },
  {
    evenement: 'marquer_fait',
    depuis: ['a_venir', 'en_preparation'],
    vers: 'faite',
    effets: ['annuler_taches_ouvertes'],
  },
  {
    evenement: 'sans_objet',
    depuis: ['a_venir', 'en_preparation'],
    vers: 'sans_objet',
    effets: ['annuler_taches_ouvertes', 'annuler_paiement_ouvert'],
  },
  {
    evenement: 'defaire',
    depuis: ['faite', 'sans_objet'],
    vers: (e) => (e.type === 'defaire' && e.tachesDemarrees ? 'en_preparation' : 'a_venir'),
    effets: ['restaurer_annules'],
  },
  {
    evenement: 'tache_rouverte',
    depuis: ['faite'],
    vers: 'en_preparation',
    effets: ['incrementer_cycle'],
  },
  {
    evenement: 'paiement_rejete',
    depuis: ['a_venir', 'en_preparation', 'faite'],
    vers: 'en_preparation',
    effets: ['incrementer_cycle', 'creer_tache_urgente'],
  },
]

export class TransitionInvalide extends Error {
  constructor(statut: string, evenement: string) {
    super(`Transition impossible : « ${evenement} » depuis « ${statut} ».`)
  }
}

export function transitionEcheance(
  etat: { statut: StatutEcheance },
  evenement: EvenementEcheance,
): { statut: StatutEcheance; effets: EffetEcheance[] } {
  const ligne = TRANSITIONS_ECHEANCE.find(
    (l) => l.evenement === evenement.type && l.depuis.includes(etat.statut),
  )
  if (!ligne) throw new TransitionInvalide(etat.statut, evenement.type)
  const statut = typeof ligne.vers === 'function' ? ligne.vers(evenement) : ligne.vers
  return { statut, effets: [...ligne.effets] }
}

// Paiement

export type EvenementPaiement =
  | { type: 'programmer' }
  | { type: 'marquer_paye' }
  /** Une pièce rattachée atteste le règlement (reçu, facture payée par carte) : niveau N0. */
  | { type: 'piece_acquittee' }
  | { type: 'demarquer_paye' }
  /** Après un rapprochement ou son annulation : couverture bancaire du montant dû. */
  | { type: 'couverture'; couvertCts: number; montantDuCts: number; marquePaye: boolean }
  | { type: 'rejet' }
  | { type: 'litige' }
  | { type: 'fin_litige' }
  | { type: 'echeance_sans_objet' }

export type EffetPaiement = 'verifier_echeance' | 'rouvrir_echeance'

type LignePaiement = {
  evenement: EvenementPaiement['type']
  depuis: readonly StatutPaiement[]
  vers: StatutPaiement | ((e: EvenementPaiement) => StatutPaiement)
  effets: readonly EffetPaiement[] | ((statut: StatutPaiement) => EffetPaiement[])
}

function statutCouvert(e: EvenementPaiement): StatutPaiement {
  if (e.type !== 'couverture') throw new Error('couverture attendue')
  if (e.couvertCts >= e.montantDuCts) return 'rapproche'
  if (e.couvertCts > 0) return 'partiel'
  return e.marquePaye ? 'paye' : 'prevu'
}

export const TRANSITIONS_PAIEMENT: readonly LignePaiement[] = [
  { evenement: 'programmer', depuis: ['prevu'], vers: 'programme', effets: [] },
  {
    evenement: 'marquer_paye',
    depuis: ['prevu', 'programme', 'partiel', 'rejete'],
    vers: 'paye',
    effets: ['verifier_echeance'],
  },
  {
    evenement: 'piece_acquittee',
    depuis: ['prevu', 'programme', 'partiel'],
    vers: 'paye',
    effets: ['verifier_echeance'],
  },
  { evenement: 'demarquer_paye', depuis: ['paye'], vers: 'prevu', effets: [] },
  {
    evenement: 'couverture',
    depuis: ['prevu', 'programme', 'partiel', 'paye', 'rapproche', 'rejete'],
    vers: statutCouvert,
    effets: (statut) => (statut === 'rapproche' ? ['verifier_echeance'] : []),
  },
  {
    evenement: 'rejet',
    depuis: ['prevu', 'programme', 'partiel', 'paye', 'rapproche'],
    vers: 'rejete',
    effets: ['rouvrir_echeance'],
  },
  {
    evenement: 'litige',
    depuis: ['prevu', 'programme', 'partiel', 'paye'],
    vers: 'en_litige',
    effets: [],
  },
  { evenement: 'fin_litige', depuis: ['en_litige'], vers: 'prevu', effets: [] },
  {
    evenement: 'echeance_sans_objet',
    depuis: ['prevu', 'programme', 'partiel', 'en_litige'],
    vers: 'annule',
    effets: [],
  },
]

export function transitionPaiement(
  etat: { statut: StatutPaiement },
  evenement: EvenementPaiement,
): { statut: StatutPaiement; effets: EffetPaiement[] } {
  const ligne = TRANSITIONS_PAIEMENT.find(
    (l) => l.evenement === evenement.type && l.depuis.includes(etat.statut),
  )
  if (!ligne) throw new TransitionInvalide(etat.statut, evenement.type)
  const statut = typeof ligne.vers === 'function' ? ligne.vers(evenement) : ligne.vers
  const effets = typeof ligne.effets === 'function' ? ligne.effets(statut) : [...ligne.effets]
  return { statut, effets }
}

// Tâche

export type EvenementTache =
  | { type: 'demarrer' }
  | { type: 'attendre_tiers' }
  | { type: 'reprendre' }
  /** portePaiement : la tâche porte un paiement encore ouvert. */
  | { type: 'terminer'; portePaiement: boolean }
  | { type: 'rouvrir' }
  | { type: 'annuler' }

/**
 * demarrer_echeance : l'échéance passe en préparation ; verifier_echeance : elle devient
 * faite si tout est fait et payé ; rouvrir_echeance : faite, elle se rouvre au cycle suivant ;
 * proposer_marquer_paye : l'interface propose de marquer le paiement payé (jamais automatique).
 */
export type EffetTache =
  'demarrer_echeance' | 'verifier_echeance' | 'rouvrir_echeance' | 'proposer_marquer_paye'

type LigneTache = {
  evenement: EvenementTache['type']
  depuis: readonly StatutTache[]
  vers: StatutTache
  effets: readonly EffetTache[] | ((e: EvenementTache) => EffetTache[])
}

const TACHE_OUVERTE = ['a_faire', 'en_cours', 'en_attente_tiers'] as const

export const TRANSITIONS_TACHE: readonly LigneTache[] = [
  { evenement: 'demarrer', depuis: ['a_faire'], vers: 'en_cours', effets: ['demarrer_echeance'] },
  {
    evenement: 'attendre_tiers',
    depuis: ['a_faire', 'en_cours'],
    vers: 'en_attente_tiers',
    effets: ['demarrer_echeance'],
  },
  { evenement: 'reprendre', depuis: ['en_attente_tiers'], vers: 'en_cours', effets: [] },
  {
    evenement: 'terminer',
    depuis: TACHE_OUVERTE,
    vers: 'faite',
    effets: (e) =>
      e.type === 'terminer' && e.portePaiement
        ? ['demarrer_echeance', 'verifier_echeance', 'proposer_marquer_paye']
        : ['demarrer_echeance', 'verifier_echeance'],
  },
  {
    evenement: 'rouvrir',
    depuis: ['faite', 'annulee'],
    vers: 'a_faire',
    effets: ['rouvrir_echeance'],
  },
  { evenement: 'annuler', depuis: TACHE_OUVERTE, vers: 'annulee', effets: ['verifier_echeance'] },
]

export function transitionTache(
  etat: { statut: StatutTache },
  evenement: EvenementTache,
): { statut: StatutTache; effets: EffetTache[] } {
  const ligne = TRANSITIONS_TACHE.find(
    (l) => l.evenement === evenement.type && l.depuis.includes(etat.statut),
  )
  if (!ligne) throw new TransitionInvalide(etat.statut, evenement.type)
  const effets = typeof ligne.effets === 'function' ? ligne.effets(evenement) : [...ligne.effets]
  return { statut: ligne.vers, effets }
}

/** L'événement qui fait passer une tâche d'un statut à l'autre, ou null s'il n'y en a pas. */
export function evenementVersStatutTache(
  depuis: StatutTache,
  vers: StatutTache,
): EvenementTache['type'] | null {
  if (depuis === vers) return null
  return (
    TRANSITIONS_TACHE.find((l) => l.vers === vers && l.depuis.includes(depuis))?.evenement ?? null
  )
}
